import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, MenuItem, TextField, Typography } from '@material-ui/core';
import { useStyles } from 'components/CustomConfirm/CustomConfirmDialog';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getCancelReasons } from 'services/booking.service';

const CancelBookingDialog = ({
  open,
  onClose = () => { },
  onConfirm = (reason) => { }
}) => {
  const classes = useStyles();

  const [cancelReasons, setCancelReasons] = useState([]);
  const [primaryReason, setPrimaryReason] = useState();
  const [secondaryReason, setSecondaryReason] = useState();

  const [loading, setLoading] = useState(false);

  const fetchReasons = useCallback(
    async () => {
      try {
        setLoading(true)
        const res = await getCancelReasons()
        setCancelReasons(res?.list || [])
        setLoading(false)
      } catch (e) {
        setLoading(false)
      }
    },
    [],
  );

  useEffect(
    () => {
      fetchReasons()
    }, []
  );

  const [isPrimaryOther, setIsPrimaryOther] = useState(false);
  const [primaryReasonOther, setPrimaryReasonOther] = useState('');
  const [secondaryReasonOther, setSecondaryReasonOther] = useState('');
  const [isSecondaryOther, setIsSecondaryOther] = useState(false);

  const renderPrimaryReason = useMemo(
    () => {
      return (
        <>
          <TextField
            fullWidth
            size="small"
            select
            variant="outlined"
            label="Primary Reason"
            InputLabelProps={{
              shrink: true,
            }}
            value={primaryReason}
            onChange={e => {
              setIsPrimaryOther(false)
              if (e.target.value === 'Other') {
                setIsPrimaryOther(true)
              }
              setSecondaryReason(null)
              setPrimaryReason(e.target.value)
            }}

          >
            {[...cancelReasons, { reason: 'Other' }]?.map(reason => (
              <MenuItem key={reason?.reason} value={reason?.reason}>
                {reason?.reason}
              </MenuItem>
            ))}
          </TextField>
          {isPrimaryOther && (
            <TextField
              autoFocus
              fullWidth
              size="small"
              variant="outlined"
              label="Other Reason"
              InputLabelProps={{
                shrink: true,
              }}
              value={primaryReasonOther}
              onChange={e => setPrimaryReasonOther(e.target.value)}
              multiline
              minRows={3}
              placeholder="Please enter your reason"
            />
          )}
        </>
      )
    }, [cancelReasons, primaryReason, isPrimaryOther, primaryReasonOther]
  );

  const renderSecondaryReason = useMemo(
    () => {
      const subReasons = cancelReasons?.find(reason => reason?.reason === primaryReason)?.sub_reasons || []
      if (subReasons?.length > 0) {
        return (
          <>
            <TextField
              fullWidth
              size="small"
              select
              variant="outlined"
              label="Secondary Reason"
              InputLabelProps={{
                shrink: true,
              }}
              value={secondaryReason}
              onChange={e => {
                setIsSecondaryOther(false)
                if (e.target.value === 'Other') {
                  setIsSecondaryOther(true)
                }
                setSecondaryReason(e.target.value)

              }}
            >
              {[...subReasons, { reason: 'Other' }]?.map(reason => (
                <MenuItem key={reason?.reason} value={reason?.reason}>
                  {reason?.reason}
                </MenuItem>
              ))}
            </TextField>
            {isSecondaryOther && (
              <TextField
                autoFocus
                fullWidth
                size="small"
                variant="outlined"
                label="Other Reason"
                InputLabelProps={{
                  shrink: true,
                }}
                value={secondaryReasonOther}
                onChange={e => setSecondaryReasonOther(e.target.value)}
                multiline
                minRows={3}
                placeholder="Please enter your reason"
              />
            )}
          </>
        )
      }
      return <></>
    }, [cancelReasons, primaryReason, secondaryReason, isSecondaryOther, secondaryReasonOther]
  );

  const isValid = useMemo(
    () => {
      const subReasons = cancelReasons?.find(reason => reason?.reason === primaryReason)?.sub_reasons || []
      if (subReasons?.length > 0) {
        if (secondaryReason === 'Other') {
          return !!primaryReason && !!secondaryReasonOther
        }
        return !!primaryReason && !!secondaryReason
      } else {
        if (primaryReason === 'Other') {
          return !!primaryReasonOther
        }
        return !!primaryReason
      }
    }, [primaryReason, secondaryReason, primaryReasonOther, secondaryReasonOther]
  );

  const doOnClose = useCallback(
    () => {
      setPrimaryReason(null)
      setSecondaryReason(null)
      setPrimaryReasonOther('')
      setSecondaryReasonOther('')
      setIsPrimaryOther(false)
      setIsSecondaryOther(false)
      onClose()
    },
    [],
  );

  const formatReason = useMemo(
    () => {
      if (primaryReason === 'Other') {
        return `OTHER: ${primaryReasonOther}`
      }
      if (!!primaryReason && secondaryReason === 'Other') {
        return `${primaryReason}: OTHER: ${secondaryReasonOther}`
      } else {
        return secondaryReason || primaryReason
      }
      return ''
    }, [secondaryReason, primaryReason, primaryReasonOther, secondaryReasonOther]
  );
  return (
    <Dialog fullWidth maxWidth="xs" open={open} onClose={() => { doOnClose() }}>
      {loading ? <CircularProgress /> : (
        <>
          <DialogTitle className={classes.header}>
            <Typography variant="h5" className={classes.dialogTitle}>
              Are you sure you want to cancel your booking?
            </Typography>
            <DialogContent>
              <DialogContentText>
                Please select a reason for cancelling your booking
              </DialogContentText>
            </DialogContent>
            <DialogContent className={classes.content}>
              {renderPrimaryReason}
              {renderSecondaryReason}
            </DialogContent>
          </DialogTitle>
          <DialogActions className={classes.actions}>
            <Button
              disabled={!isValid}
              fullWidth
              color="primary"
              variant="contained"
              onClick={() => {
                onConfirm(formatReason)
                doOnClose()
              }}
            >
              Yes, Cancel
            </Button>
            <Button
              fullWidth
              color="default"
              variant="text"
              onClick={() => {
                doOnClose()
              }}
            >
              Changed my mind
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  )

}


const CancelGroupBooking = ({ confirm, booking, onConfirm = () => { } }) => {
  return confirm(cprops => (
    <CustomConfirmDialog
      {...cprops}
      description={`Are you sure you want to cancel this booking ${booking?.id} containing ${booking?.list?.length} bookings?`}
      title="Cancel Group Booking"
      confirmationText="Confirm Cancel"
      cancellationText="Go back"
      onConfirm={() => {
        cprops.onClose()
        onConfirm()
      }}
      onCancel={() => {
        cprops.onClose()
      }}
    />
  ))
}

export { CancelBookingDialog, CancelGroupBooking };
